import React from 'react';
import { motion } from 'framer-motion';
import IndustryPageHeading from '../components/IndustryPageHeading';
import EnterpriseVideo from '../components/EnterpriseVideo';

/**
 * Government & Public Safety Solutions — internal route
 * /solutions/industries/government
 *
 * Three content sections with alternating layout direction (text/video),
 * mirroring the first three sections of the Logistics Solutions page and
 * matching the other industry subpages (Insurance / Real Estate / Retail
 * / Healthcare / Finance / Manufacturing). Each video slot is an empty
 * champagne-gold-bordered placeholder frame labeled "Video Demo Coming
 * Soon", alternating sides (video right, then left, then right). After
 * the three sections the page goes directly to the site's standard
 * footer, which is rendered globally by Layout.
 */

const VideoDemoFrame = ({ cloudflareVideoId }) => {
  if (!cloudflareVideoId) {
    return (
      <div className="w-full">
        <div
          className="relative w-full overflow-hidden rounded-lg bg-gradient-to-br from-[#E8E4DC] to-[#E8EDE7]"
          style={{
            aspectRatio: '16 / 9',
            border: '2px solid #D4AF37',
            boxShadow:
              'inset 0 2px 8px rgba(20, 30, 48, 0.15), 0 4px 12px rgba(0, 0, 0, 0.1)',
          }}
          data-testid="government-video-placeholder"
        >
          <div className="absolute inset-0 flex items-center justify-center">
            <p className="text-base sm:text-lg text-black/60 font-medium tracking-wide">
              Video Demo Coming Soon
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <EnterpriseVideo
        cloudflareVideoId={cloudflareVideoId}
        title="Government demo video"
        testId={`government-video-${cloudflareVideoId}`}
        className="w-full rounded-lg bg-gradient-to-br from-[#E8E4DC] to-[#E8EDE7]"
        style={{
          aspectRatio: '16 / 9',
          border: '2px solid #D4AF37',
          boxShadow:
            'inset 0 2px 8px rgba(20, 30, 48, 0.15), 0 4px 12px rgba(0, 0, 0, 0.1)',
        }}
      />
    </div>
  );
};

const FeatureBlock = ({ title, description }) => (
  <div className="pl-4 relative">
    <div
      className="absolute left-0 top-1 bottom-1 w-0.5 rounded-full"
      style={{
        background: 'linear-gradient(to bottom, #D4AF37, rgba(212, 175, 55, 0.4))',
      }}
    />
    <h3 className="text-base sm:text-lg font-medium text-black/90">{title}</h3>
    <p className="text-sm text-black/70 mt-1">{description}</p>
  </div>
);

const TextContent = ({ eyebrow, heading, body, features, isPageHeading }) => {
  const HeadingTag = isPageHeading ? 'h1' : 'h2';
  return (
    <div>
      <p className="text-sm md:text-base font-bold text-black/90 mb-3 md:mb-4 uppercase tracking-wider">
        {eyebrow}
      </p>
      <HeadingTag className="text-3xl md:text-4xl font-medium text-black/90 mb-4 md:mb-6">
        {heading}
      </HeadingTag>
      <p className="text-base sm:text-lg text-black/70 mb-8 md:mb-10">{body}</p>
      <div className="space-y-6">
        {features.map((f) => (
          <FeatureBlock key={f.title} title={f.title} description={f.description} />
        ))}
      </div>
    </div>
  );
};

const sections = [
  {
    eyebrow: 'Citizen Services',
    cloudflareVideoId: '07494d0b43b7bce9e0a627e3873f6d10',
    heading: 'Every request, answered without the wait.',
    body:
      'GoogLyn handles constituent inquiries, intake, and permit or license processing around the clock \u2014 cutting the wait times that erode public trust.',
    features: [
      {
        title: '24/7 constituent support',
        description: 'Common questions answered instantly, any time of day.',
      },
      {
        title: 'Automated request intake',
        description: "Cases created and routed the moment they're submitted.",
      },
      {
        title: 'Faster permit & license processing',
        description: 'Applications reviewed and approved without a backlog.',
      },
    ],
  },
  {
    eyebrow: 'Operations & Response',
    cloudflareVideoId: '8e2f9eac63b62e7d74df47e67f5960f4',
    heading: 'Every case, routed to the right desk instantly.',
    body:
      'GoogLyn manages case assignment, coordinates resources, and organizes records automatically \u2014 so response teams spend time solving problems, not searching for files.',
    features: [
      {
        title: 'Automated case management',
        description: 'Cases routed and tracked in real time.',
      },
      {
        title: 'Resource coordination',
        description: 'Dispatch and staffing support handled automatically.',
      },
      {
        title: 'Centralized records management',
        description: 'Data organized and accessible instantly across departments.',
      },
    ],
  },
  {
    eyebrow: 'Compliance & Reporting',
    cloudflareVideoId: 'e754db225eab7b737eaaa4549d8e1e04',
    heading: 'Every record, always audit-ready.',
    body:
      'Regulatory compliance monitoring, incident analytics, and reporting run continuously, so administrators work from real-time data instead of reconstructing it after the fact.',
    features: [
      {
        title: 'Regulatory compliance monitoring',
        description: 'Audit trails stay current automatically.',
      },
      {
        title: 'Real-time public reporting',
        description: 'Operational data always at your fingertips.',
      },
      {
        title: 'Incident analytics',
        description: 'Risk patterns flagged before they escalate.',
      },
    ],
  },
];

const IndustryGovernment = () => {
  return (
    <div className="w-full" data-testid="government-solutions-page">
      <IndustryPageHeading name="Government & Public Safety" testId="government-page-heading" />
      {sections.map((section, idx) => {
        const textFirst = idx % 2 === 0; // sections 1,3 → text left (video right); 2 → video left
        const bg = idx % 2 === 0 ? 'bg-white' : 'bg-[#F5F7F5]';
        return (
          <section
            key={section.eyebrow}
            className={`py-16 md:py-24 ${bg}`}
            data-testid={`government-section-${idx + 1}`}
          >
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center"
              >
                {textFirst ? (
                  <>
                    <TextContent {...section} isPageHeading={idx === 0} />
                    <VideoDemoFrame cloudflareVideoId={section.cloudflareVideoId} />
                  </>
                ) : (
                  <>
                    {/* Video first on desktop; text stays above video on mobile for readability */}
                    <div className="order-2 lg:order-1">
                      <VideoDemoFrame cloudflareVideoId={section.cloudflareVideoId} />
                    </div>
                    <div className="order-1 lg:order-2">
                      <TextContent {...section} isPageHeading={idx === 0} />
                    </div>
                  </>
                )}
              </motion.div>
            </div>
          </section>
        );
      })}
    </div>
  );
};

export default IndustryGovernment;
